import React, { useState, useEffect } from 'react';
import { DollarSign, Save, RotateCcw, Info, Plus, Trash2, AlertTriangle } from 'lucide-react';
import { LTSSService } from '../../types/ltss';

// Procedure code rate configuration
export interface ProcedureRateConfig {
  procedureCode: string;
  description: string;
  ratePerUnit: number;
  isOverride: boolean; // true = user set, false = derived from data
}

export interface ProcedureRatesConfig {
  [procedureCode: string]: ProcedureRateConfig;
}

const PROCEDURE_RATES_STORAGE = 'ltss_procedure_rates';

// Get saved procedure rate overrides
export function getProcedureRateOverrides(): ProcedureRatesConfig {
  try {
    const saved = localStorage.getItem(PROCEDURE_RATES_STORAGE);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (e) {
    console.error('Failed to load procedure rates:', e);
  }
  return {};
}

// Save procedure rate overrides
export function saveProcedureRateOverrides(rates: ProcedureRatesConfig): void {
  localStorage.setItem(PROCEDURE_RATES_STORAGE, JSON.stringify(rates));
}

// Derive rates from paid claims - this is the core function for accurate calculations
export function deriveRatesFromPaidClaims(services: LTSSService[]): ProcedureRatesConfig {
  const derived: ProcedureRatesConfig = {};
  const aggregates: Record<string, { totalPaid: number; totalUnits: number; descriptions: Set<string> }> = {};

  // Aggregate paid claims by procedure code
  services.forEach(service => {
    if (
      (service.claimHistory === 'Original Claim Paid' || service.claimHistory === 'Adjustment Claim Paid') &&
      service.amountPaid && service.amountPaid > 0 &&
      service.unitsBilled && service.unitsBilled > 0 &&
      service.procedureCode
    ) {
      const code = service.procedureCode.trim();
      if (!aggregates[code]) {
        aggregates[code] = { totalPaid: 0, totalUnits: 0, descriptions: new Set() };
      }
      aggregates[code].totalPaid += service.amountPaid;
      aggregates[code].totalUnits += service.unitsBilled;
      if (service.serviceType) {
        aggregates[code].descriptions.add(service.serviceType);
      }
    }
  });

  // Calculate rate per unit for each procedure code
  Object.entries(aggregates).forEach(([code, data]) => {
    if (data.totalUnits > 0) {
      const rate = data.totalPaid / data.totalUnits;
      derived[code] = {
        procedureCode: code,
        description: Array.from(data.descriptions).join(', ') || code,
        ratePerUnit: Math.round(rate * 100) / 100, // Round to 2 decimal places
        isOverride: false,
      };
    }
  });

  return derived;
}

// Get effective rates: user overrides take precedence over derived rates
export function getEffectiveRates(services: LTSSService[]): ProcedureRatesConfig {
  const derived = deriveRatesFromPaidClaims(services);
  const overrides = getProcedureRateOverrides();

  // Merge: overrides take precedence
  const effective: ProcedureRatesConfig = { ...derived };
  Object.entries(overrides).forEach(([code, override]) => {
    effective[code] = {
      ...override,
      isOverride: true,
    };
  });

  return effective;
}

// Calculate estimated value for a service using effective rates
export function calculateServiceValue(
  service: LTSSService,
  effectiveRates: ProcedureRatesConfig
): { value: number; hasRate: boolean; rate: number } {
  const units = service.unitsBilled || 0;
  const code = service.procedureCode?.trim() || '';
  const rateConfig = effectiveRates[code];

  if (rateConfig) {
    return {
      value: units * rateConfig.ratePerUnit,
      hasRate: true,
      rate: rateConfig.ratePerUnit,
    };
  }

  // No rate found - return 0 with warning flag
  return {
    value: 0,
    hasRate: false,
    rate: 0,
  };
}

interface ProcedureRatesProps {
  services?: LTSSService[];
}

export function ProcedureRates({ services = [] }: ProcedureRatesProps) {
  const [derivedRates, setDerivedRates] = useState<ProcedureRatesConfig>({});
  const [overrides, setOverrides] = useState<ProcedureRatesConfig>({});
  const [newCode, setNewCode] = useState('');
  const [newRate, setNewRate] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  // Load derived rates and overrides
  useEffect(() => {
    const derived = deriveRatesFromPaidClaims(services);
    setDerivedRates(derived);
    setOverrides(getProcedureRateOverrides());
  }, [services]);

  const handleOverrideRate = (code: string, rate: string) => {
    const numRate = parseFloat(rate);
    if (isNaN(numRate) || numRate < 0) return;

    const existing = derivedRates[code] || { procedureCode: code, description: code, ratePerUnit: 0, isOverride: false };
    setOverrides(prev => ({
      ...prev,
      [code]: {
        ...existing,
        ratePerUnit: numRate,
        isOverride: true,
      },
    }));
    setHasChanges(true);
  };

  const handleRemoveOverride = (code: string) => {
    setOverrides(prev => {
      const next = { ...prev };
      delete next[code];
      return next;
    });
    setHasChanges(true);
  };

  const handleAddCustomRate = () => {
    if (!newCode.trim() || !newRate.trim()) return;
    const numRate = parseFloat(newRate);
    if (isNaN(numRate) || numRate < 0) return;

    setOverrides(prev => ({
      ...prev,
      [newCode.trim()]: {
        procedureCode: newCode.trim(),
        description: newDescription.trim() || newCode.trim(),
        ratePerUnit: numRate,
        isOverride: true,
      },
    }));
    setNewCode('');
    setNewRate('');
    setNewDescription('');
    setHasChanges(true);
  };

  const handleSave = () => {
    saveProcedureRateOverrides(overrides);
    setHasChanges(false);
  };

  const handleReset = () => {
    setOverrides({});
    saveProcedureRateOverrides({});
    setHasChanges(false);
  };

  // Combine derived and overrides for display
  const allCodes = new Set([...Object.keys(derivedRates), ...Object.keys(overrides)]);
  const displayRates = Array.from(allCodes).map(code => {
    const derived = derivedRates[code];
    const override = overrides[code];
    return {
      code,
      derivedRate: derived?.ratePerUnit || null,
      effectiveRate: override?.ratePerUnit ?? derived?.ratePerUnit ?? null,
      description: override?.description || derived?.description || code,
      isOverride: !!override,
    };
  }).sort((a, b) => a.code.localeCompare(b.code));

  const codesWithoutRates = displayRates.filter(r => r.effectiveRate === null);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-green-400" />
          <h3 className="text-lg font-medium text-slate-200">Procedure Code Rates</h3>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleReset}
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-slate-400 hover:text-slate-200 
                     hover:bg-slate-700 rounded-lg transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            Reset All
          </button>
          <button
            onClick={handleSave}
            disabled={!hasChanges}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-green-600 hover:bg-green-700 
                     disabled:bg-slate-700 disabled:text-slate-500 text-white rounded-lg transition-colors"
          >
            <Save className="w-4 h-4" />
            Save
          </button>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
        <div className="flex items-start gap-2">
          <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="text-blue-400 font-medium">How Rates Work</p>
            <p className="text-slate-400 mt-1">
              Rates are automatically derived from your paid claims data. You can override any rate 
              if needed. Overrides are highlighted in green. These rates are used for leakage 
              calculations and financial projections.
            </p>
          </div>
        </div>
      </div>

      {/* Warning for codes without rates */}
      {codesWithoutRates.length > 0 && (
        <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="text-yellow-400 font-medium">
                {codesWithoutRates.length} procedure code(s) without rates
              </p>
              <p className="text-slate-400 mt-1">
                These codes have no paid claims to derive rates from. Add manual rates below for accurate calculations.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Add Custom Rate */}
      <div className="bg-slate-800/50 rounded-lg p-4">
        <h4 className="text-sm font-medium text-slate-300 mb-3">Add Custom Rate</h4>
        <div className="flex gap-2 flex-wrap">
          <input
            type="text"
            value={newCode}
            onChange={(e) => setNewCode(e.target.value.toUpperCase())}
            placeholder="Procedure Code (e.g., T1019)"
            className="w-32 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-200 
                     placeholder-slate-500 text-sm focus:outline-none focus:border-green-500"
          />
          <input
            type="text"
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            placeholder="Description (optional)"
            className="flex-1 min-w-[150px] px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-200 
                     placeholder-slate-500 text-sm focus:outline-none focus:border-green-500"
          />
          <div className="flex items-center gap-1">
            <span className="text-slate-400">$</span>
            <input
              type="number"
              step="0.01"
              min="0"
              value={newRate}
              onChange={(e) => setNewRate(e.target.value)}
              placeholder="Rate/unit"
              className="w-24 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-200 
                       placeholder-slate-500 text-sm focus:outline-none focus:border-green-500"
            />
          </div>
          <button
            onClick={handleAddCustomRate}
            disabled={!newCode.trim() || !newRate.trim()}
            className="flex items-center gap-1 px-4 py-2 bg-green-600 hover:bg-green-700 
                     disabled:bg-slate-700 disabled:text-slate-500 text-white rounded-lg transition-colors text-sm"
          >
            <Plus className="w-4 h-4" />
            Add
          </button>
        </div>
      </div>

      {/* Rates Table */}
      <div className="bg-slate-800/50 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-700/50">
              <th className="text-left px-4 py-3 text-slate-400 font-medium">Procedure Code</th>
              <th className="text-left px-4 py-3 text-slate-400 font-medium">Description</th>
              <th className="text-right px-4 py-3 text-slate-400 font-medium">Derived Rate</th>
              <th className="text-right px-4 py-3 text-slate-400 font-medium">Effective Rate</th>
              <th className="text-center px-4 py-3 text-slate-400 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {displayRates.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                  No procedure codes found. Upload a report to see derived rates.
                </td>
              </tr>
            ) : (
              displayRates.map((rate) => (
                <tr key={rate.code} className={`border-t border-slate-700 ${rate.isOverride ? 'bg-green-500/5' : ''}`}>
                  <td className="px-4 py-3">
                    <code className="text-blue-400 bg-slate-700 px-2 py-0.5 rounded">{rate.code}</code>
                  </td>
                  <td className="px-4 py-3 text-slate-300">{rate.description}</td>
                  <td className="px-4 py-3 text-right text-slate-400">
                    {rate.derivedRate !== null ? `$${rate.derivedRate.toFixed(2)}` : '—'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <span className="text-slate-400">$</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={rate.effectiveRate ?? ''}
                        onChange={(e) => handleOverrideRate(rate.code, e.target.value)}
                        className={`w-20 px-2 py-1 bg-slate-700 border rounded text-right text-sm
                                  focus:outline-none focus:border-green-500
                                  ${rate.isOverride ? 'border-green-500 text-green-400' : 'border-slate-600 text-slate-200'}`}
                      />
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {rate.isOverride && (
                      <button
                        onClick={() => handleRemoveOverride(rate.code)}
                        className="p-1 text-slate-400 hover:text-red-400 transition-colors"
                        title="Remove override"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
